import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, ArrowRight, Trash2 } from "lucide-react";
import Layout from "@/components/Layout";
import { API } from "@/contexts/AuthContext";
import { formatarCnj } from "@/lib/masks";
import { Button } from "@/components/ui/button";
import { DADOS } from "../lib/larguras";

// Pré-cadastro: a antessala entre a busca por OAB e os processos do escritório.
//
// Tem tela própria, e não só um diálogo, porque a terceira saída é não decidir.
// Quem fecha a janela precisa de um caminho de volta — sem ele, "fica
// aguardando" vira "some".

const MESES_PARA_SUGERIR = 12;

const recente = (iso) => {
  if (!iso) return false;
  const quando = new Date(iso);
  if (isNaN(quando.getTime())) return false;
  const limite = new Date();
  limite.setMonth(limite.getMonth() - MESES_PARA_SUGERIR);
  return quando >= limite;
};


const formatarData = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
};

export default function PreCadastroPage() {
  const [processos, setProcessos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [marcados, setMarcados] = useState(() => new Set());
  const [soRecentes, setSoRecentes] = useState(true);
  const [agindo, setAgindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/processos/pre-cadastro`, { withCredentials: true });
      const lista = res.data?.processos || [];
      setProcessos(lista);
      setMarcados(new Set(
        lista.filter((p) => recente(p.capa?.ultima_movimentacao)).map((p) => p.numero_cnj),
      ));
    } catch {
      setProcessos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const visiveis = soRecentes
    ? processos.filter((p) => recente(p.capa?.ultima_movimentacao))
    : processos;
  const ocultos = processos.length - visiveis.length;

  const alternar = (cnj) => setMarcados((anterior) => {
    const proximo = new Set(anterior);
    proximo.has(cnj) ? proximo.delete(cnj) : proximo.add(cnj);
    return proximo;
  });

  const decidir = async (acao) => {
    if (marcados.size === 0) return;
    setAgindo(true);
    try {
      const res = await axios.post(
        `${API}/processos/pre-cadastro/${acao}`,
        { numeros: Array.from(marcados) },
        { withCredentials: true },
      );
      if (acao === "importar") {
        toast.success(`${res.data?.total_importados || 0} processo(s) movido(s) para os seus processos`);
        if (res.data?.total_sem_espaco) {
          toast.warning(`${res.data.total_sem_espaco} não couberam no limite do seu plano e continuam aqui.`);
        }
      } else {
        toast.success(`${res.data?.dispensados || 0} processo(s) excluído(s)`);
      }
      await carregar();
    } catch (falha) {
      toast.error(falha?.response?.data?.detail || "Não foi possível concluir agora.");
    } finally {
      setAgindo(false);
    }
  };

  return (
    <Layout>
      <div className={DADOS}>
        <p className="text-xs font-semibold tracking-widest text-[#6685AD] uppercase mb-1">Processos</p>
        <h1 className="text-4xl font-bold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
          Fila de processos
        </h1>
        <p className="text-[#335480] mb-8 max-w-2xl">
          Processos encontrados pela busca por OAB. Eles <strong>ainda não estão</strong> na sua
          lista de processos e não contam no limite do seu plano. Marque e escolha:
          mover para os seus processos ou excluir. O que você não decidir agora fica aqui.
        </p>

        {carregando ? (
          <div className="h-40 bg-[#F4F7FB] rounded-lg animate-pulse" />
        ) : processos.length === 0 ? (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-16 text-center">
            <Inbox size={48} className="mx-auto mb-4 text-[#D1DEEE]" aria-hidden="true" />
            <h2 className="text-xl font-semibold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
              Nada aguardando decisão
            </h2>
            <p className="text-[#335480]">
              Quando você buscar processos por OAB, os encontrados aparecem aqui antes
              de entrar na sua lista.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-[#D1DEEE]">
              <label className="flex items-center gap-2 text-sm text-[#335480]">
                <input type="checkbox" checked={soRecentes} data-testid="filtro-recentes"
                       onChange={(e) => setSoRecentes(e.target.checked)} />
                Só com movimentação no último ano
                {ocultos > 0 && <span className="text-[#6685AD]">({ocultos} ocultos)</span>}
              </label>
              <button type="button" className="text-sm text-[#003882] underline"
                      onClick={() => setMarcados(
                        marcados.size === visiveis.length
                          ? new Set()
                          : new Set(visiveis.map((p) => p.numero_cnj)),
                      )}>
                {marcados.size === visiveis.length ? "Desmarcar todos" : "Marcar todos"}
              </button>
            </div>

            <div className="divide-y divide-[#D1DEEE] max-h-[55vh] overflow-y-auto" data-testid="lista-pre-cadastro">
              {visiveis.length === 0 && (
                <p className="p-8 text-center text-sm text-[#335480]">
                  Nenhum processo com esse filtro.
                </p>
              )}
              {visiveis.map((p) => (
                <label key={p.numero_cnj} className="flex items-start gap-3 p-4 cursor-pointer hover:bg-[#F4F7FB]">
                  <input type="checkbox" className="mt-1" checked={marcados.has(p.numero_cnj)}
                         onChange={() => alternar(p.numero_cnj)} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#001533]">
                      {formatarCnj(p.numero_cnj)}
                      {p.capa?.ativo === false && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#F4F7FB] text-[#6685AD]">arquivado</span>
                      )}
                    </div>
                    <div className="text-sm text-[#335480] truncate">
                      {[p.capa?.classe, p.capa?.orgao_julgador, p.capa?.tribunal].filter(Boolean).join(" · ") || "Sem dados de capa"}
                    </div>
                    <div className="text-xs text-[#6685AD]">
                      {formatarData(p.capa?.ultima_movimentacao)
                        ? `Última movimentação: ${formatarData(p.capa.ultima_movimentacao)}`
                        : "Sem movimentação informada"}
                      {p.oab && ` · da OAB ${p.oab}`}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-[#D1DEEE]">
              <Button onClick={() => decidir("dispensar")} disabled={agindo || marcados.size === 0}
                      data-testid="excluir-selecionados" variant="outline"
                      className="border-[#D1DEEE] text-[#335480] font-semibold">
                <Trash2 size={16} />
                Excluir {marcados.size || ""}
              </Button>
              <Button onClick={() => decidir("importar")} disabled={agindo || marcados.size === 0}
                      data-testid="mover-selecionados"
                      className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                {agindo ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {agindo ? "Movendo…" : `Mover ${marcados.size} para meus processos`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
